import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  ShoppingCart, 
  ArrowLeft, 
  Plus, 
  DollarSign, 
  CreditCard, 
  User, 
  Phone, 
  Calendar, 
  RefreshCw, 
  Ban, 
  FileText,
  Trash2,
  Loader2,
  Edit,
  X,
  Save,
  Search
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import { customerService, productService } from '@/lib/packages';
import type { Database } from '@/integrations/supabase/types';

// Database types
type SaleRow = Database['public']['Tables']['sales']['Row'];
type SaleItemRow = Database['public']['Tables']['sale_items']['Row'];
type SalePaymentRow = Database['public']['Tables']['sale_payments']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];
type ProductRow = Database['public']['Tables']['products']['Row'];
type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];

interface SaleLineItem {
  id: string;
  productId?: string;
  productName: string;
  productCode: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  subtotal: number;
  note?: string;
}

interface Payment {
  id: string;
  amount: number;
  method: Database['public']['Enums']['payment_type_enum'];
  reference?: string;
  date: Date;
  status: 'completed' | 'pending' | 'failed';
  note?: string;
}

interface Adjustment {
  id: string;
  type: 'refund' | 'discount';
  amount: number;
  reason: string;
  date: Date;
  authorizedBy: string;
}

interface SaleDetail {
  id: string;
  date: Date;
  customerId?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  salesPerson: string;
  status: Database['public']['Enums']['sale_status_enum'];
  paymentStatus: 'paid' | 'partial' | 'pending' | 'overdue';
  lineItems: SaleLineItem[];
  payments: Payment[];
  adjustments: Adjustment[];
  notes: string;
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
}

// Helper functions
const determinePaymentStatus = (sale: SaleRow): 'paid' | 'partial' | 'pending' | 'overdue' => {
  if (sale.total_paid >= sale.total_net) return 'paid';
  if (sale.total_paid > 0) return 'partial';
  if (sale.balance_due > 0) {
    const daysSinceSale = (new Date().getTime() - new Date(sale.sale_datetime).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceSale > 30 ? 'overdue' : 'pending';
  }
  return 'pending';
};

const statusConfig: Record<Database['public']['Enums']['sale_status_enum'], { label: string; color: string }> = {
  open: { label: 'Açık', color: 'bg-primary/20 text-primary-foreground border-primary/30' },
  completed: { label: 'Tamamlandı', color: 'bg-success/20 text-success-foreground border-success/30' },
  cancelled: { label: 'İptal Edildi', color: 'bg-destructive/20 text-destructive-foreground border-destructive/30' },
  refunded: { label: 'İade Edildi', color: 'bg-warning/20 text-warning-foreground border-warning/30' }
};

const paymentStatusConfig = {
  paid: { label: 'Ödendi', color: 'bg-success/20 text-success-foreground border-success/30' },
  partial: { label: 'Kısmi', color: 'bg-warning/20 text-warning-foreground border-warning/30' },
  pending: { label: 'Beklemede', color: 'bg-primary/20 text-primary-foreground border-primary/30' },
  overdue: { label: 'Vadesi Geçmiş', color: 'bg-destructive/20 text-destructive-foreground border-destructive/30' }
};

const paymentMethodConfig: Record<Database['public']['Enums']['payment_type_enum'], { label: string; icon: any }> = {
  cash: { label: 'Nakit', icon: DollarSign },
  credit_card: { label: 'Kredi Kartı', icon: CreditCard },
  odero: { label: 'Odero', icon: CreditCard },
  paro: { label: 'Paro', icon: CreditCard },
  transfer: { label: 'Transfer', icon: CreditCard },
  promissory: { label: 'Senet', icon: FileText },
  other: { label: 'Diğer', icon: DollarSign }
};

export default function SaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, loading: authLoading, user } = useAuth();
  const { currentLocation } = useLocation();
  
  const [saleData, setSaleData] = useState<SaleDetail | null>(null);
  const [originalSaleData, setOriginalSaleData] = useState<SaleDetail | null>(null);
  const [originalSaleItems, setOriginalSaleItems] = useState<Array<{id: string; productId: string; quantity: number}>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit mode states
  const [editCustomerSearch, setEditCustomerSearch] = useState('');
  const [editCustomers, setEditCustomers] = useState<CustomerRow[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [editingProductForItem, setEditingProductForItem] = useState<string | null>(null);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProducts, setEditProducts] = useState<ProductRow[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  const [newPayment, setNewPayment] = useState({
    amount: 0,
    method: 'cash' as Payment['method'],
    reference: '',
    note: ''
  });
  
  const [cancelReason, setCancelReason] = useState('');

  // Fetch sale data from database
  const fetchSaleData = async () => {
    if (!id || !companyId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch sale with related data
      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .select(`
          *,
          customers(first_name, last_name, phone, email),
          user_profiles!sales_created_by_fkey(full_name)
        `)
        .eq('id', id)
        .eq('company_id', companyId)
        .single();
      
      if (saleError) throw saleError;
      if (!saleData) throw new Error('Satış bulunamadı');
      
      // Fetch sale items with product info
      const { data: saleItemsData, error: saleItemsError } = await supabase
        .from('sale_items')
        .select(`
          *,
          products(id, name, item_code)
        `)
        .eq('sale_id', id)
        .eq('company_id', companyId);
      
      if (saleItemsError) throw saleItemsError;
      
      // Store original sale items for inventory tracking
      const originalItems = (saleItemsData || []).map(item => ({
        id: item.id,
        productId: item.product_id,
        quantity: item.quantity
      }));
      setOriginalSaleItems(originalItems);
      
      // Fetch sale payments
      const { data: salePaymentsData, error: salePaymentsError } = await supabase
        .from('sale_payments')
        .select('*')
        .eq('sale_id', id)
        .eq('company_id', companyId)
        .order('paid_at', { ascending: false });
      
      if (salePaymentsError) throw salePaymentsError;
      
      // Transform data to interface format
      const customer = saleData.customers;
      const userProfile = saleData.user_profiles;
      
      const lineItems: SaleLineItem[] = (saleItemsData || []).map(item => ({
        id: item.id,
        productId: item.product_id,
        productName: item.products?.name || 'Bilinmeyen Ürün',
        productCode: item.products?.item_code || '',
        quantity: item.quantity,
        unitPrice: item.sale_price_each || 0,
        discount: item.discount,
        subtotal: item.line_total_net
      }));
      
      const payments: Payment[] = (salePaymentsData || []).map(payment => ({
        id: payment.id,
        amount: payment.amount,
        method: payment.payment_type,
        reference: payment.ref || undefined,
        date: new Date(payment.paid_at),
        status: 'completed',
        note: undefined
      }));
      
      const transformedSale: SaleDetail = {
        id: saleData.id,
        date: new Date(saleData.sale_datetime),
        customerId: saleData.customer_id || undefined,
        customerName: customer 
          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
          : 'Bilinmeyen Müşteri',
        customerPhone: customer?.phone || '',
        customerEmail: customer?.email || undefined,
        salesPerson: userProfile?.full_name || 'Bilinmeyen',
        status: saleData.status,
        paymentStatus: determinePaymentStatus(saleData),
        lineItems,
        payments,
        adjustments: [], // Initialize as empty array for now
        notes: saleData.note || '',
        subtotal: saleData.total_gross,
        totalDiscount: saleData.total_discount,
        totalTax: saleData.total_gross - saleData.total_net,
        totalAmount: saleData.total_net,
        amountPaid: saleData.total_paid,
        balanceDue: saleData.balance_due
      };
      
      setSaleData(transformedSale);
      setOriginalSaleData(JSON.parse(JSON.stringify(transformedSale))); // Deep copy
      
    } catch (err) {
      console.error('Error fetching sale data:', err);
      setError(err instanceof Error ? err.message : 'Satış verileri yüklenirken bir hata oluştu');
      toast({
        title: "Hata",
        description: "Satış verileri yüklenirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && companyId && !authLoading) {
      void fetchSaleData();
    }
  }, [id, companyId, authLoading]);

  // Customer search for edit mode
  const searchCustomersForEdit = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setEditCustomers([]);
      return;
    }
    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setEditCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId]);

  // Product search for edit mode
  const searchProductsForEdit = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setEditProducts([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setEditProducts(results);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, [companyId]);

  // Effect for customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (editCustomerSearch && isEditMode) {
        searchCustomersForEdit(editCustomerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [editCustomerSearch, isEditMode, searchCustomersForEdit]);

  // Effect for product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (editProductSearch && isEditMode && editingProductForItem) {
        searchProductsForEdit(editProductSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [editProductSearch, isEditMode, editingProductForItem, searchProductsForEdit]);

  const handleAddPayment = async () => {
    if (!saleData || !companyId) return;
    
    if (newPayment.amount <= 0) {
      toast({
        title: "Geçersiz Tutar",
        description: "Ödeme tutarı sıfırdan büyük olmalıdır",
        variant: "destructive",
      });
      return;
    }

    try {
      // Insert new payment into database
      const { data, error } = await supabase
        .from('sale_payments')
        .insert({
          sale_id: saleData.id,
          company_id: companyId,
          amount: newPayment.amount,
          payment_type: newPayment.method,
          ref: newPayment.reference || null,
          paid_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update sale totals
      const newAmountPaid = saleData.amountPaid + newPayment.amount;
      const newBalanceDue = saleData.balanceDue - newPayment.amount;
      
      const { error: updateError } = await supabase
        .from('sales')
        .update({
          total_paid: newAmountPaid,
          balance_due: newBalanceDue
        })
        .eq('id', saleData.id)
        .eq('company_id', companyId);
      
      if (updateError) throw updateError;

      // Log activity for payment
      if (companyId) {
        await logActivity({
          action: 'payment',
          entity: 'sale',
          entityId: saleData.id,
          detail: {
            amount: newPayment.amount,
            payment_type: newPayment.method,
            total_paid: newAmountPaid,
            balance_due: newBalanceDue,
          },
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }
      
      // Update local state
      const payment: Payment = {
        id: data.id,
        amount: newPayment.amount,
        method: newPayment.method,
        reference: newPayment.reference || undefined,
        date: new Date(),
        status: 'completed',
        note: newPayment.note || undefined
      };

      setSaleData(prev => prev ? ({
        ...prev,
        payments: [...prev.payments, payment],
        amountPaid: newAmountPaid,
        balanceDue: newBalanceDue
      }) : null);

      setNewPayment({ amount: 0, method: 'cash', reference: '', note: '' });
      setShowAddPayment(false);
      
      toast({
        title: "Ödeme Eklendi",
        description: `₺${payment.amount} ödeme kaydedildi`,
      });
    } catch (err) {
      console.error('Error adding payment:', err);
      toast({
        title: "Hata",
        description: "Ödeme eklenirken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  const handleUpdateSale = async () => {
    if (!saleData || !companyId || !originalSaleData) return;

    setSaving(true);
    try {
      // Calculate new totals
      const newSubtotal = saleData.lineItems.reduce((sum, item) => {
        const lineGross = item.quantity * item.unitPrice;
        return sum + lineGross;
      }, 0);
      
      const newTotalDiscount = saleData.lineItems.reduce((sum, item) => sum + item.discount, 0);
      const newTotalNet = newSubtotal - newTotalDiscount;
      const newBalanceDue = newTotalNet - saleData.amountPaid;

      // Update sale record
      const { error: updateSaleError } = await supabase
        .from('sales')
        .update({
          customer_id: saleData.customerId || null,
          sale_datetime: saleData.date.toISOString(),
          status: saleData.status,
          note: saleData.notes || null,
          total_gross: newSubtotal,
          total_discount: newTotalDiscount,
          total_net: newTotalNet,
          balance_due: newBalanceDue,
        })
        .eq('id', saleData.id)
        .eq('company_id', companyId);

      if (updateSaleError) throw updateSaleError;

      // Handle inventory adjustments
      // First, reverse all original inventory deductions
      for (const originalItem of originalSaleItems) {
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('quantity_on_hand')
          .eq('id', originalItem.productId)
          .eq('company_id', companyId)
          .single();

        if (productError) throw productError;

        const newQuantity = (product?.quantity_on_hand || 0) + originalItem.quantity;
        
        const { error: reverseError } = await supabase
          .from('products')
          .update({ quantity_on_hand: newQuantity })
          .eq('id', originalItem.productId)
          .eq('company_id', companyId);

        if (reverseError) throw reverseError;

        // Create inventory movement for reversal
        await supabase
          .from('inventory_movements')
          .insert({
            company_id: companyId,
            product_id: originalItem.productId,
            movement_type: 'adjustment_in',
            qty: originalItem.quantity,
            related_table: 'sale_update',
            related_id: saleData.id,
            actor_user_id: user?.id || null,
          });
      }

      // Delete old sale items
      const { error: deleteItemsError } = await supabase
        .from('sale_items')
        .delete()
        .eq('sale_id', saleData.id)
        .eq('company_id', companyId);

      if (deleteItemsError) throw deleteItemsError;

      // Create new sale items and deduct inventory
      for (const item of saleData.lineItems) {
        if (!item.productId) continue;

        // Calculate line totals
        const lineGross = item.quantity * item.unitPrice;
        const lineNet = lineGross - item.discount;

        // Create sale item
        const { error: itemError } = await supabase
          .from('sale_items')
          .insert({
            company_id: companyId,
            sale_id: saleData.id,
            product_id: item.productId,
            quantity: item.quantity,
            sale_price_each: item.unitPrice,
            discount: item.discount,
            line_total_gross: lineGross,
            line_total_net: lineNet,
          });

        if (itemError) throw itemError;

        // Deduct inventory
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('quantity_on_hand')
          .eq('id', item.productId)
          .eq('company_id', companyId)
          .single();

        if (productError) throw productError;

        const currentQuantity = product?.quantity_on_hand || 0;
        const newQuantity = currentQuantity - item.quantity; // Allow negative stock

        const { error: inventoryError } = await supabase
          .from('products')
          .update({ quantity_on_hand: newQuantity })
          .eq('id', item.productId)
          .eq('company_id', companyId);

        if (inventoryError) throw inventoryError;

        // Create inventory movement
        await supabase
          .from('inventory_movements')
          .insert({
            company_id: companyId,
            product_id: item.productId,
            movement_type: 'sale_out',
            qty: item.quantity,
            related_table: 'sales',
            related_id: saleData.id,
            actor_user_id: user?.id || null,
          });
      }

      // Update local state with new totals
      setSaleData(prev => prev ? {
        ...prev,
        subtotal: newSubtotal,
        totalDiscount: newTotalDiscount,
        totalAmount: newTotalNet,
        balanceDue: newBalanceDue
      } : null);

      // Update original sale items
      const newOriginalItems = saleData.lineItems
        .filter(item => item.productId)
        .map(item => ({
          id: item.id,
          productId: item.productId!,
          quantity: item.quantity
        }));
      setOriginalSaleItems(newOriginalItems);

      // Refresh sale data
      await fetchSaleData();
      setIsEditMode(false);

      toast({
        title: "Satış Güncellendi",
        description: "Satış başarıyla güncellendi",
      });
    } catch (err) {
      console.error('Error updating sale:', err);
      toast({
        title: "Hata",
        description: "Satış güncellenirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSale = async () => {
    if (!saleData || !companyId) return;

    setSaving(true);
    try {
      // First, check if this sale was created from onhold records
      const { data: linkedOnHoldRecords, error: onHoldCheckError } = await supabase
        .from('on_holds')
        .select('id, product_id, hold_quantity')
        .eq('linked_sale_id', saleData.id)
        .eq('company_id', companyId)
        .eq('status', 'converted');

      if (onHoldCheckError) throw onHoldCheckError;

      // First, get all sale items to reverse inventory
      const { data: saleItems, error: saleItemsError } = await supabase
        .from('sale_items')
        .select(`
          *,
          products(id, name, quantity_on_hand)
        `)
        .eq('sale_id', saleData.id)
        .eq('company_id', companyId);

      if (saleItemsError) throw saleItemsError;

      // Reverse inventory for each item
      for (const item of saleItems || []) {
        if (item.products) {
          const newQuantity = (item.products.quantity_on_hand || 0) + item.quantity;
          
          // Update product quantity
          const { error: updateError } = await supabase
            .from('products')
            .update({ quantity_on_hand: newQuantity })
            .eq('id', item.products.id)
            .eq('company_id', companyId);

          if (updateError) throw updateError;

          // Create inventory movement record
          const { error: movementError } = await supabase
            .from('inventory_movements')
            .insert({
              company_id: companyId,
              product_id: item.products.id,
              movement_type: 'adjustment_in',
              qty: item.quantity,
              related_table: 'sale_deletion',
              related_id: saleData.id,
              actor_user_id: user?.id || null,
            });

          if (movementError) throw movementError;
        }
      }

      // If this sale was created from onhold records, revert them back to 'on_hold' status
      if (linkedOnHoldRecords && linkedOnHoldRecords.length > 0) {
        for (const onHoldRecord of linkedOnHoldRecords) {
          const { error: revertOnHoldError } = await supabase
            .from('on_holds')
            .update({
              status: 'on_hold',
              linked_sale_id: null
            })
            .eq('id', onHoldRecord.id)
            .eq('company_id', companyId);

          if (revertOnHoldError) throw revertOnHoldError;
        }
      }

      // Delete sale payments
      const { error: deletePaymentsError } = await supabase
        .from('sale_payments')
        .delete()
        .eq('sale_id', saleData.id)
        .eq('company_id', companyId);

      if (deletePaymentsError) throw deletePaymentsError;

      // Delete sale items
      const { error: deleteItemsError } = await supabase
        .from('sale_items')
        .delete()
        .eq('sale_id', saleData.id)
        .eq('company_id', companyId);

      if (deleteItemsError) throw deleteItemsError;

      // Delete sale
      const { error: deleteSaleError } = await supabase
        .from('sales')
        .delete()
        .eq('id', saleData.id)
        .eq('company_id', companyId);

      if (deleteSaleError) throw deleteSaleError;

      // Log activity
      if (companyId) {
        await logActivity({
          action: 'delete',
          entity: 'sale',
          entityId: saleData.id,
          detail: {
            customer_name: saleData.customerName,
            total_amount: saleData.totalAmount,
          },
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }

      const message = linkedOnHoldRecords && linkedOnHoldRecords.length > 0
        ? "Satış başarıyla silindi, stok miktarları geri yüklendi ve beklemedeki kayıtlar geri alındı"
        : "Satış başarıyla silindi ve stok miktarları geri yüklendi";

      toast({
        title: "Satış Silindi",
        description: message,
      });

      // Navigate back to sales list
      navigate('/sales');
    } catch (err) {
      console.error('Error deleting sale:', err);
      toast({
        title: "Hata",
        description: "Satış silinirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setShowDeleteDialog(false);
    }
  };

  const handleAddLineItem = () => {
    if (!saleData) return;
    const newItem: SaleLineItem = {
      id: `temp-${Date.now()}`,
      productId: '',
      productName: '',
      productCode: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      subtotal: 0
    };
    setSaleData({
      ...saleData,
      lineItems: [...saleData.lineItems, newItem]
    });
  };

  const handleRemoveLineItem = (itemId: string) => {
    if (!saleData) return;
    setSaleData({
      ...saleData,
      lineItems: saleData.lineItems.filter(item => item.id !== itemId)
    });
  };

  const handleUpdateLineItem = (itemId: string, updates: Partial<SaleLineItem>) => {
    if (!saleData) return;
    setSaleData({
      ...saleData,
      lineItems: saleData.lineItems.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, ...updates };
          // Recalculate subtotal
          const lineGross = updated.quantity * updated.unitPrice;
          updated.subtotal = lineGross - updated.discount;
          return updated;
        }
        return item;
      })
    });
  };

  const handleSelectProduct = (itemId: string, product: ProductRow) => {
    handleUpdateLineItem(itemId, {
      productId: product.id,
      productName: product.name,
      productCode: product.item_code || '',
      unitPrice: product.price || 0
    });
  };

  const handleCancelEdit = () => {
    if (originalSaleData) {
      setSaleData(JSON.parse(JSON.stringify(originalSaleData)));
    }
    setIsEditMode(false);
    setEditCustomerSearch('');
    setEditProductSearch('');
    setEditingProductForItem(null);
    setEditCustomers([]);
    setEditProducts([]);
  };

  const handleCancelSale = async () => {
    if (!saleData || !companyId) return;
    
    if (!cancelReason.trim()) {
      toast({
        title: "İptal Sebebi Gerekli",
        description: "Satışı iptal etmek için sebep belirtmeniz gerekiyor",
        variant: "destructive",
      });
      return;
    }

    try {
      // First, check if this sale was created from onhold records
      const { data: linkedOnHoldRecords, error: onHoldCheckError } = await supabase
        .from('on_holds')
        .select('id, product_id, hold_quantity')
        .eq('linked_sale_id', saleData.id)
        .eq('company_id', companyId)
        .eq('status', 'converted');

      if (onHoldCheckError) throw onHoldCheckError;

      // First, get all sale items to reverse inventory
      const { data: saleItems, error: saleItemsError } = await supabase
        .from('sale_items')
        .select(`
          *,
          products(id, name, quantity_on_hand)
        `)
        .eq('sale_id', saleData.id)
        .eq('company_id', companyId);

      if (saleItemsError) throw saleItemsError;

      // Reverse inventory for each item
      for (const item of saleItems || []) {
        if (item.products) {
          const newQuantity = (item.products.quantity_on_hand || 0) + item.quantity;
          
          // Update product quantity
          const { error: updateError } = await supabase
            .from('products')
            .update({ quantity_on_hand: newQuantity })
            .eq('id', item.products.id)
            .eq('company_id', companyId);

          if (updateError) throw updateError;

          // Create inventory movement record
          const { error: movementError } = await supabase
            .from('inventory_movements')
            .insert({
              company_id: companyId,
              product_id: item.products.id,
              movement_type: 'adjustment_in',
              qty: item.quantity,
              related_table: 'sale_cancellation',
              related_id: saleData.id,
              actor_user_id: user?.id || null,
            });

          if (movementError) throw movementError;
        }
      }

      // If this sale was created from onhold records, revert them back to 'on_hold' status
      if (linkedOnHoldRecords && linkedOnHoldRecords.length > 0) {
        for (const onHoldRecord of linkedOnHoldRecords) {
          const { error: revertOnHoldError } = await supabase
            .from('on_holds')
            .update({
              status: 'on_hold',
              linked_sale_id: null
            })
            .eq('id', onHoldRecord.id)
            .eq('company_id', companyId);

          if (revertOnHoldError) throw revertOnHoldError;
        }
      }

      // Update sale status to cancelled
      const { error: updateSaleError } = await supabase
        .from('sales')
        .update({ 
          status: 'cancelled',
          note: (saleData.notes ? saleData.notes + '\n\n' : '') + `İptal edildi: ${cancelReason}`
        })
        .eq('id', saleData.id)
        .eq('company_id', companyId);

      if (updateSaleError) throw updateSaleError;

      // Update local state
      setSaleData(prev => prev ? ({ 
        ...prev, 
        status: 'cancelled',
        notes: (prev.notes ? prev.notes + '\n\n' : '') + `İptal edildi: ${cancelReason}`
      }) : null);

      setCancelReason('');
      setShowCancelDialog(false);
      
      const message = linkedOnHoldRecords && linkedOnHoldRecords.length > 0
        ? "Satış başarıyla iptal edildi, stok miktarları geri yüklendi ve beklemedeki kayıtlar geri alındı"
        : "Satış başarıyla iptal edildi ve stok miktarları geri yüklendi";
      
      toast({
        title: "Satış İptal Edildi",
        description: message,
      });
    } catch (err) {
      console.error('Error cancelling sale:', err);
      toast({
        title: "Hata",
        description: "Satış iptal edilirken bir hata oluştu",
        variant: "destructive",
      });
    }
  };


  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Satış detayları yükleniyor...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !saleData) {
    return (
      <Layout>
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sales')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Satışlara Dön
          </Button>
          
          <div className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Satış bulunamadı</h3>
            <p className="text-muted-foreground mb-4">{error || 'Bu satış mevcut değil'}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={fetchSaleData} variant="outline">
                Tekrar Dene
              </Button>
              <Button onClick={() => navigate('/sales')}>
                Satışlara Dön
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sales')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Satışlara Dön
          </Button>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">Satış - {saleData.customerName}</h1>
              <Badge variant="outline" className={statusConfig[saleData.status].color}>
                {statusConfig[saleData.status].label}
              </Badge>
              <Badge variant="outline" className={paymentStatusConfig[saleData.paymentStatus].color}>
                {paymentStatusConfig[saleData.paymentStatus].label}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {format(saleData.date, 'd MMMM yyyy', { locale: tr })} • Satış Yapan: {saleData.salesPerson}
            </p>
          </div>

          <div className="flex gap-2">
            {!isEditMode ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditMode(true)}
                  disabled={saleData.status === 'cancelled'}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
                <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Ödeme Ekle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ödeme Ekle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="paymentAmount">Tutar *</Label>
                    <Input
                      id="paymentAmount"
                      type="number"
                      value={newPayment.amount || ''}
                      onChange={(e) => setNewPayment(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>Ödeme Yöntemi</Label>
                    <Select value={newPayment.method} onValueChange={(value: Payment['method']) => 
                      setNewPayment(prev => ({ ...prev, method: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Nakit</SelectItem>
                        <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                        <SelectItem value="odero">Odero</SelectItem>
                        <SelectItem value="paro">Paro</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="promissory">Senet</SelectItem>
                        <SelectItem value="other">Diğer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="paymentReference">Referans</Label>
                    <Input
                      id="paymentReference"
                      value={newPayment.reference}
                      onChange={(e) => setNewPayment(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="İşlem ID, çek numarası, vb."
                    />
                  </div>
                  <div>
                    <Label htmlFor="paymentNote">Not</Label>
                    <Textarea
                      id="paymentNote"
                      value={newPayment.note}
                      onChange={(e) => setNewPayment(prev => ({ ...prev, note: e.target.value }))}
                      placeholder="Ödeme notları..."
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowAddPayment(false)}>
                      İptal
                    </Button>
                    <Button className="flex-1" onClick={handleAddPayment}>
                      Ödeme Ekle
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {saleData.status !== 'cancelled' && (
              <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <Ban className="w-4 h-4 mr-2" />
                    İptal Et
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Satışı İptal Et</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-sm text-destructive-foreground">
                      <strong>Uyarı:</strong> Bu işlem geri alınamaz. Satış iptal edilecek ve tüm ürünlerin stok miktarları geri yüklenecektir.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="cancelReason">İptal Sebebi *</Label>
                    <Textarea
                      id="cancelReason"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Satışın iptal edilme sebebini açıklayın..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowCancelDialog(false)}>
                      Vazgeç
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="flex-1" 
                      onClick={handleCancelSale} 
                      disabled={!cancelReason.trim() || saleData?.status === 'cancelled'}
                    >
                      Satışı İptal Et
                    </Button>
                  </div>
                </div>
              </DialogContent>
              </Dialog>
            )}
            {saleData.status !== 'cancelled' && (
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Sil
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Satışı Sil</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive-foreground">
                        <strong>Uyarı:</strong> Bu işlem geri alınamaz. Satış tamamen silinecek ve tüm ürünlerin stok miktarları geri yüklenecektir.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => setShowDeleteDialog(false)}>
                        Vazgeç
                      </Button>
                      <Button 
                        variant="destructive" 
                        className="flex-1" 
                        onClick={handleDeleteSale}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Satışı Sil
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button 
                  onClick={handleUpdateSale}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Kaydet
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Müşteri Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Müşteri Ara</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={editCustomerSearch}
                          onChange={(e) => setEditCustomerSearch(e.target.value)}
                          placeholder="Müşteri adı veya telefon ile ara..."
                          className="pl-9"
                        />
                      </div>
                      {editCustomerSearch.length >= 2 && (
                        <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                          {loadingCustomers ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                              Aranıyor...
                            </div>
                          ) : editCustomers.length > 0 ? (
                            editCustomers.map(customer => (
                              <div
                                key={customer.id}
                                className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                                onClick={() => {
                                  setSaleData(prev => prev ? {
                                    ...prev,
                                    customerId: customer.id,
                                    customerName: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri',
                                    customerPhone: customer.phone || '',
                                    customerEmail: customer.email || undefined
                                  } : null);
                                  setEditCustomerSearch('');
                                  setEditCustomers([]);
                                }}
                              >
                                <div className="font-medium">
                                  {`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri'}
                                </div>
                                {customer.phone && (
                                  <div className="text-sm text-muted-foreground">{customer.phone}</div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              Müşteri bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{saleData.customerName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{saleData.customerPhone}</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{saleData.customerName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{saleData.customerPhone}</span>
                    </div>
                    {saleData.customerEmail && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">@</span>
                        <span>{saleData.customerEmail}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Ürünler ({saleData.lineItems.length})</CardTitle>
                  {isEditMode && (
                    <Button variant="outline" size="sm" onClick={handleAddLineItem}>
                      <Plus className="w-4 h-4 mr-2" />
                      Ürün Ekle
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {saleData.lineItems.map((item) => (
                    <div key={item.id} className="flex items-start justify-between p-3 border rounded-lg">
                      {isEditMode ? (
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label>Ürün</Label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                value={item.productName || ''}
                                onChange={(e) => {
                                  if (!item.productId) {
                                    setEditingProductForItem(item.id);
                                    setEditProductSearch(e.target.value);
                                    handleUpdateLineItem(item.id, { productName: e.target.value });
                                  }
                                }}
                                onFocus={() => {
                                  if (!item.productId) {
                                    setEditingProductForItem(item.id);
                                  }
                                }}
                                placeholder="Ürün adı veya kod ile ara..."
                                className="pl-9"
                              />
                            </div>
                            {editProductSearch.length >= 2 && editingProductForItem === item.id && !item.productId && (
                              <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                                {loadingProducts ? (
                                  <div className="p-4 text-center text-sm text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                                    Aranıyor...
                                  </div>
                                ) : editProducts.length > 0 ? (
                                  editProducts.map(product => (
                                    <div
                                      key={product.id}
                                      className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                                      onClick={() => {
                                        handleSelectProduct(item.id, product);
                                        setEditProductSearch('');
                                        setEditProducts([]);
                                        setEditingProductForItem(null);
                                      }}
                                    >
                                      <div className="font-medium">{product.name}</div>
                                      {product.item_code && (
                                        <div className="text-sm text-muted-foreground">{product.item_code}</div>
                                      )}
                                      <div className="text-sm font-medium">₺{product.price?.toLocaleString('tr-TR') || '0'}</div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-4 text-center text-sm text-muted-foreground">
                                    Ürün bulunamadı
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label>Miktar</Label>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleUpdateLineItem(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="1"
                              />
                            </div>
                            <div>
                              <Label>Birim Fiyat</Label>
                              <Input
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => handleUpdateLineItem(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div>
                              <Label>İndirim</Label>
                              <Input
                                type="number"
                                value={item.discount}
                                onChange={(e) => handleUpdateLineItem(item.id, { discount: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              Toplam: ₺{item.subtotal.toLocaleString('tr-TR')}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveLineItem(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            <h4 className="font-medium">{item.productName}</h4>
                            <p className="text-sm text-muted-foreground">{item.productCode}</p>
                            {item.note && (
                              <p className="text-sm text-muted-foreground mt-1">Not: {item.note}</p>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center gap-4 text-sm">
                              <span>{item.quantity} × ₺{item.unitPrice.toLocaleString('tr-TR')}</span>
                              {item.discount > 0 && (
                                <span className="text-success">-₺{item.discount.toLocaleString('tr-TR')}</span>
                              )}
                              <span className="font-medium">₺{item.subtotal.toLocaleString('tr-TR')}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Ödemeler ({saleData.payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {saleData.payments.map((payment) => {
                    const methodConfig = paymentMethodConfig[payment.method];
                    const Icon = methodConfig.icon;
                    
                    return (
                      <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">₺{payment.amount.toLocaleString('tr-TR')}</div>
                            <div className="text-sm text-muted-foreground">
                              {methodConfig.label} • {format(payment.date, 'd MMM yyyy', { locale: tr })}
                              {payment.reference && ` • ${payment.reference}`}
                            </div>
                            {payment.note && (
                              <div className="text-sm text-muted-foreground">{payment.note}</div>
                            )}
                          </div>
                        </div>
                        
                        <Badge variant={payment.status === 'completed' ? 'default' : 'secondary'}>
                          {payment.status === 'completed' ? 'Tamamlandı' : payment.status === 'pending' ? 'Beklemede' : payment.status}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Adjustments */}
            {saleData.adjustments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Düzeltmeler</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {saleData.adjustments.map((adjustment) => (
                      <div key={adjustment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">
                            {adjustment.type === 'refund' ? 'İade' : adjustment.type === 'discount' ? 'İndirim' : adjustment.type}
                          </div>
                          <div className="text-sm text-muted-foreground">{adjustment.reason}</div>
                          <div className="text-sm text-muted-foreground">
                            {format(adjustment.date, 'd MMM yyyy', { locale: tr })} • {adjustment.authorizedBy} tarafından
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-medium ${adjustment.type === 'refund' ? 'text-destructive' : 'text-success'}`}>
                            {adjustment.type === 'refund' ? '-' : ''}₺{adjustment.amount.toLocaleString('tr-TR')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Notlar
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <Textarea
                    value={saleData.notes}
                    onChange={(e) => setSaleData(prev => prev ? { ...prev, notes: e.target.value } : null)}
                    placeholder="Satış notları..."
                    rows={4}
                  />
                ) : (
                  <p className="text-sm">{saleData.notes || 'Not yok'}</p>
                )}
              </CardContent>
            </Card>

            {/* Status (in edit mode) */}
            {isEditMode && (
              <Card>
                <CardHeader>
                  <CardTitle>Durum</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={saleData.status}
                    onValueChange={(value: Database['public']['Enums']['sale_status_enum']) => 
                      setSaleData(prev => prev ? { ...prev, status: value } : null)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Açık</SelectItem>
                      <SelectItem value="completed">Tamamlandı</SelectItem>
                      <SelectItem value="cancelled">İptal Edildi</SelectItem>
                      <SelectItem value="refunded">İade Edildi</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Date (in edit mode) */}
            {isEditMode && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Satış Tarihi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="datetime-local"
                    value={format(saleData.date, "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => {
                      const newDate = e.target.value ? new Date(e.target.value) : new Date();
                      setSaleData(prev => prev ? { ...prev, date: newDate } : null);
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Satış Özeti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  // Calculate totals dynamically in edit mode
                  const calculatedSubtotal = isEditMode 
                    ? saleData.lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0)
                    : saleData.subtotal;
                  const calculatedDiscount = isEditMode
                    ? saleData.lineItems.reduce((sum, item) => sum + item.discount, 0)
                    : saleData.totalDiscount;
                  const calculatedTotal = calculatedSubtotal - calculatedDiscount;
                  const calculatedBalance = calculatedTotal - saleData.amountPaid;
                  
                  return (
                    <>
                      <div className="flex justify-between">
                        <span>Ara Toplam:</span>
                        <span>₺{calculatedSubtotal.toLocaleString('tr-TR')}</span>
                      </div>
                      
                      {calculatedDiscount > 0 && (
                        <div className="flex justify-between text-success">
                          <span>İndirim:</span>
                          <span>-₺{calculatedDiscount.toLocaleString('tr-TR')}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <span>Vergi:</span>
                        <span>₺{(calculatedSubtotal - calculatedTotal).toLocaleString('tr-TR')}</span>
                      </div>
                      
                      <Separator />
                      
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Toplam:</span>
                        <span>₺{calculatedTotal.toLocaleString('tr-TR')}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span>Ödenen Tutar:</span>
                        <span>₺{saleData.amountPaid.toLocaleString('tr-TR')}</span>
                      </div>
                      
                      <div className="flex justify-between font-medium">
                        <span>Kalan Bakiye:</span>
                        <span className={calculatedBalance > 0 ? 'text-destructive' : calculatedBalance < 0 ? 'text-success' : ''}>
                          ₺{Math.abs(calculatedBalance).toLocaleString('tr-TR')}
                          {calculatedBalance > 0 && ' borç'}
                          {calculatedBalance < 0 && ' kredi'}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}